/**
 * @file 内容卡片
 */
import { useFm } from '@web3/base-hooks';
import { AccountPoolInfo } from '@/types/account';
import { PoolIcons } from '@/components/pool/poolIcons';
import { Button } from '@bfe/moly';
import cls from 'classnames';
import { MultiIcon } from '@/components/multiIcon';
import { useParams } from '@web3/base-hooks';
import { CoinInfo, CoinDetailJson } from '@/types/common';
import styles from './index.module.scss';
import { appPath } from '@web3/base-utils';

interface CardProps {
  data: AccountPoolInfo;
}

export const Card = ({ data }: CardProps) => {
  const fm = useFm();
  const { locale } = useParams();
  const {
    lpCoinDetail,
    lpTokens,
    processingUnstakeAmount,
    claimableAmount,
    extraYieldDTO,
    poolIcons,
    chainCode,
    poolName,
  } = data || {};

  const { investment, baseYield, rewardYield } = extraYieldDTO || {};

  let iconList: CoinInfo[] | { coinDetailJson: CoinDetailJson }[] =
    data.baseStakeCoins;

  if (chainCode === 'SOL' && Array.isArray(poolIcons) && poolIcons?.length) {
    iconList = [{ coinDetailJson: poolIcons?.[0] }];
  }

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        {/* 池子信息 */}
        <div className={cls(styles.centerRow, styles.liquidityPool)}>
          <PoolIcons
            iconList={iconList}
            extraIconUrl={data.icon?.night}
            className={styles.poolIcons}
            poolName={poolName}
          />
          <div className={styles.poolInfoArea}>
            <div className={styles.poolName}>{data.poolName}</div>
            <div className={styles.poolType}>{data.poolType}</div>
          </div>
        </div>
        {/* 按钮区 */}
        <div className={styles.buttonArea}>
          <Button
            variant="primary"
            size="x-small"
            className={styles.button}
            onClick={() => {
              if (window.top) {
                window.top.location.href = `/${locale}${appPath}/pool/${data.poolId}`;
              }
            }}
          >
            {fm('details')}
          </Button>
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.cell}>
          {/* 持仓份额 */}
          <div className={styles.rowBetween}>
            <div className={styles.label}>{fm('investmentBreakdown')}</div>
            <MultiIcon
              data={[
                {
                  url: lpCoinDetail.iconNightUrl,
                  amount: lpTokens,
                  symbol: lpCoinDetail.symbol,
                },
              ]}
            />
          </div>
          {/* 我的投资 */}
          <div className={cls(styles.rowBetween, 'mt-4')}>
            <div className={cls(styles.label, styles.row)}>
              {fm('staking_my_investments')}
              {/* 正在赎回的币种，针对不能立即赎回，需要T+N天才能赎回的池子 */}
              {!!+processingUnstakeAmount && (
                <div className={styles.processingUnstakeAmount}>
                  {fm('redeemProcessingNum', {
                    num: `${processingUnstakeAmount} ${lpCoinDetail.symbol}`,
                  })}
                </div>
              )}
              {!!+claimableAmount && (
                <div className={styles.withdrawableFunds}>
                  {fm('assetsAvailableForWithdrawal')}
                </div>
              )}
            </div>
            {investment?.length > 0 ? (
              <MultiIcon
                data={investment?.map((item) => ({
                  url: item.coinDetailJson?.iconNightUrl,
                  amount: item.amount,
                  symbol: item.coinDetailJson?.symbol as string,
                }))}
              />
            ) : (
              '--'
            )}
          </div>
        </div>
        <div className={styles.line} />
        {/* 基础收益 */}
        <div className={styles.cell}>
          <div className={styles.rowBetween}>
            <div className={styles.label}>{fm('baseReturns')}</div>
            {baseYield?.length > 0 ? (
              <MultiIcon
                data={baseYield?.map((item) => ({
                  url: item.coinDetailJson?.iconNightUrl,
                  amount: item.amount,
                  symbol: item.coinDetailJson?.symbol as string,
                }))}
              />
            ) : (
              '--'
            )}
          </div>
          {/* 奖励金 */}
          <div className={cls(styles.rowBetween, 'mt-4')}>
            <div className={cls(styles.label, styles.row)}>
              {fm('rewardMoney')}
              {rewardYield?.length > 0 && (
                <div className={styles.withdrawableFunds}>
                  {fm('rewardAvailable')}
                </div>
              )}
            </div>
            {rewardYield?.length > 0 ? (
              <MultiIcon
                data={rewardYield?.map((item) => ({
                  url: item.coinDetailJson?.iconNightUrl,
                  amount: item.amount,
                  symbol: item.coinDetailJson?.symbol as string,
                }))}
              />
            ) : (
              '--'
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
